import React, { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext.jsx";
import { useSaved } from "../context/SavedContext.jsx";
import { useHeader } from "../context/HeaderContext.jsx";
import {
  toggleSearchModal,
  getSearchModalShown,
  subscribeSearchModalShown,
  getSearchModalMode,
  toggleShowcaseModal,
  closeSearchModal,
} from "../utils/searchModalSignal.js";
import { primeSoftKeyboard } from "../utils/softKeyboard.js";
import { fetchBrandList, warmShowcase } from "../utils/brandList.js";
import { getDisplayedPath, subscribeDisplayedPath } from "../utils/displayedRoute.js";
import { useAudio } from "../context/AudioContext.jsx";
import SavedWidget from "./SavedWidget.jsx";

import CartWidget from "./CartWidget.jsx";
import CurrencySelector from "./CurrencySelector.jsx";
import { requestBrandNav } from "../utils/brandNavSignal.js";
import faviconUrl from "../assets/favicon.png";
import { BRANDS_MENU_ENABLED, MUSIC_ENABLED, MUSIC_HEADER_ENABLED } from "../config.js";
import { fetchItems, fetchShowcaseHiddenBrands } from "../utils/inventoryWarmup.js";
import { useIsMobile } from "../hooks/useIsMobile.js";
import { isTouchDevice } from "../utils/viewport.js";
import { lockScroll, unlockScroll } from "../utils/scrollLock.js";

// The first entry is the brand carousel, now SHOWCASE at /showcase (it was
// BRANDS at /brands). Its key stays "brand": the hooks on it (nav-brand, the
// hover menu, the brand-index-open press) are all keyed on that. The separate
// Showcase page this name used to belong to is retired.
// The catalog proper: on one of these, CATALOG scrolls up instead of
// navigating (every other /featured page goes back to /featured).
const CATALOG_VIEWS = new Set(["/featured", "/featured/new", "/featured/sold"]);

const HEADER_PAGES = [
  { key: "brand", label: "SHOWCASE", path: "/showcase" },
  { key: "featured", label: "CATALOG", path: "/featured" },
];

// Bar order, left to right: HOME · CATALOG · SHOWCASE (HOME is injected before
// the first entry, in the nav below). The row sits in the header's LEFT cell,
// immediately right of the hamburger — not centred in the bar.
//
// HEADER_PAGES itself is deliberately NOT reordered: activePageIndex and
// handlePageSlotChange both address a page by its index in THAT array, so
// reordering the source would silently point the underline and the click
// handler at the wrong section. This carries each page's original index
// along instead. Anything not named here falls in
// after these in HEADER_PAGES order.
const NAV_KEY_ORDER = ["featured", "brand"];
const navRank = (key) => {
  const i = NAV_KEY_ORDER.indexOf(key);
  return i === -1 ? NAV_KEY_ORDER.length : i;
};
const HEADER_NAV = HEADER_PAGES
  .map((page, index) => ({ page, index }))
  .sort((a, b) => navRank(a.page.key) - navRank(b.page.key) || a.index - b.index);


// Sidebar footer rows. Contact is the wordmark itself (a mailto link), so
// the only row is Shipping & Returns, which lands on /info where that is
// written up.
const DRAWER_FOOT_LINKS = [
  { label: "Shipping & Returns", path: "/info" },
  // A `mailto` entry instead of a `path`: this row hands off to whatever the
  // reader's device has set as its mail client rather than routing anywhere in
  // the app. No form and no sending from our side, so the message arrives from
  // their own address and their reply-to is correct by construction.
  { label: "Contact", mailto: "contact@itrapswag.com" },
];

// The longest a pressed section link waits for the loading bar to reach it
// (Header's pendingNav) before lighting anyway.
const NAV_PENDING_MAX_MS = 1500;
// Length of the bar's own sweep on a section press; matches headerNavSweep
// in app.css.
const NAV_SWEEP_MS = 800;

export default function Header({ collapsed = false, landingIntro = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isOpen: savedOpen } = useSaved();
  const { loading: headerLoading } = useHeader();
  const headerRef = useRef(null);
  const [loadingPhase, setLoadingPhase] = useState("idle"); // idle | loading | finishing
  const finishTimerRef = useRef(null);

  // Drive the header loading bar: slow fill while loading, snap-to-100% on ready
  useEffect(() => {
    if (headerLoading) {
      setLoadingPhase("loading");
      return;
    }
    setLoadingPhase((prev) => {
      if (prev !== "loading") return prev;
      // Capture current ::after width so the finish animation starts where the fill left off
      if (headerRef.current) {
        const cs = window.getComputedStyle(headerRef.current, "::after");
        const widthPx = parseFloat(cs.getPropertyValue("width")) || 0;
        const headerWidth = headerRef.current.offsetWidth || 1;
        const pct = Math.max(0, Math.min(92, (widthPx / headerWidth) * 100));
        headerRef.current.style.setProperty("--header-load-from", `${pct}%`);
      }
      clearTimeout(finishTimerRef.current);
      finishTimerRef.current = setTimeout(() => setLoadingPhase("idle"), 500);
      return "finishing";
    });
  }, [headerLoading]);

  useEffect(() => () => clearTimeout(finishTimerRef.current), []);

  // A pressed section link (HOME / CATALOG / SHOWCASE) waits for the loading
  // bar: grey, no underline even under the pointer, until the bar's front has
  // run past its right edge, and only then lights as the current section (the
  // owner, 2026-10-25: "only show white once the white line has gone past
  // it"). The bar only runs while a page reports loading, which a section
  // change on warm data often never does, so a press with the bar idle kicks
  // one sweep of its own: the finish animation, from 0%. A real load that
  // starts takes over with its own fill. NAV_PENDING_MAX_MS is the cap, so
  // a bar that never arrives cannot leave the section unlit.
  // The sweep is its own phase ("sweep", .app-header--loading-sweep), slower
  // off the mark than the finish snap: that one crossed the first link in
  // ~40ms, too soon to see the word go out and come back.
  const [pendingNav, setPendingNav] = useState(null); // nav key, or "home"
  const loadingPhaseRef = useRef(loadingPhase);
  loadingPhaseRef.current = loadingPhase;
  const startNavPending = useCallback((key) => {
    setPendingNav(key);
    if (loadingPhaseRef.current !== "idle") return;
    clearTimeout(finishTimerRef.current);
    finishTimerRef.current = setTimeout(
      () => setLoadingPhase((prev) => (prev === "sweep" ? "idle" : prev)),
      NAV_SWEEP_MS,
    );
    setLoadingPhase("sweep");
  }, []);
  useEffect(() => {
    if (!pendingNav) return undefined;
    const header = headerRef.current;
    const link = header?.querySelector(`[data-header-interaction="nav-${pendingNav}"]`);
    if (!header || !link) {
      setPendingNav(null);
      return undefined;
    }
    const started = performance.now();
    let frame = 0;
    const tick = () => {
      const bar = window.getComputedStyle(header, "::after");
      const front = header.getBoundingClientRect().left + (parseFloat(bar.width) || 0);
      const passed = parseFloat(bar.opacity) > 0 && front >= link.getBoundingClientRect().right;
      if (passed || performance.now() - started > NAV_PENDING_MAX_MS) {
        setPendingNav(null);
        return;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [pendingNav]);
  const [swoop, setSwoop] = useState(() => ({
    center: 0,
    width: 0,
    height: 0,
    id: null,
    visible: false,
  }));
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);

  const prevSectionRef = useRef(1);
  const activePageIndex = useMemo(() => {
    const idx = HEADER_PAGES.findIndex((p) =>
      location.pathname.startsWith(p.path)
    );
    // On item detail pages, keep the previous section if it was brands
    if (location.pathname.includes("/featured/item/") && prevSectionRef.current === 0) {
      return 0;
    }
    if (idx >= 0) {
      prevSectionRef.current = idx;
      return idx;
    }
    // No matching header section (e.g. /terms, /privacy, /contact, /faq):
    // fall back to the last known section so the index is always valid.
    return prevSectionRef.current;
  }, [location.pathname, location.search]);

  // Whether this page belongs to a section at all. activePageIndex falls
  // back to the last one visited (the phone's index must stay valid), and
  // that fallback used to underline CATALOG on /contact, /legal and the
  // rest; the bar's mark now needs a real match.
  const onSectionPage = HEADER_PAGES.some((p) => location.pathname.startsWith(p.path));

  const handlePageSlotChange = useCallback((index) => {
    const page = HEADER_PAGES[index];
    if (page) {
      navigate(page.path);
      // Delay scroll reset until after the page transition fade
      setTimeout(() => window.scrollTo({ top: 0 }), 160);
    }
  }, [navigate]);

  // Desktop header extras: the search icon, which opens the site search
  // modal (SearchModal in App.jsx, via utils/searchModalSignal), and the
  // music toggle (shared AudioContext). The drawer's search row is the
  // other way in.
  const audio = useAudio();
  const audioPlaying = !!audio?.playing;
  const toggleAudio = audio?.toggle;
  const [menuOpen, setMenuOpen] = useState(false);
  // Phone width, live (hooks/useIsMobile): the same query the CSS branches
  // on, so the React tree and the stylesheet flip together.
  const isMobile = useIsMobile();
  const lastScrollY = useRef(0);
  const [drawerSearchOpen, setDrawerSearchOpen] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [brandsSubOpen, setBrandsSubOpen] = useState(false);

  // BRANDS hover menu. The list is every brand we carry live stock for, read
  // from the same warm inventory cache /brand and the landing slices use, so
  // opening it usually costs no request at all. Fetched on FIRST hover rather
  // than on mount: the bar is on every page and most visits never open this.
  const [brandsMenuOpen, setBrandsMenuOpen] = useState(false);
  const [brandNames, setBrandNames] = useState([]);
  const brandsRequestedRef = useRef(false);

  // Loads the brand list once, on first demand. Shared by the header hover
  // menu and the sidebar BRANDS dropdown, so whichever opens first pays for
  // the fetch and the other finds the list already there. Before this split
  // only the hover menu triggered it, so a visitor who went straight to the
  // sidebar got an empty BRANDS list.
  const ensureBrandNames = useCallback(() => {
    if (brandsRequestedRef.current) return;
    brandsRequestedRef.current = true;
    Promise.all([fetchItems(), fetchShowcaseHiddenBrands()])
      .then(([items, hidden]) => {
        // The same rule BrandCarousel's brandRows applies: a brand counts
        // once site manager has assigned it (brand_id) and it has live
        // stock, and not if the owner hid it from the Showcase, so the menu
        // can never offer a brand whose page would come up empty.
        const names = new Set();
        for (const item of items) {
          if (!item.brand_id) continue;
          const name = (item.brand_name || "").toLowerCase().trim();
          if (name && !hidden.has(name)) names.add(name);
        }
        setBrandNames([...names].sort((a, b) => a.localeCompare(b)));
      })
      .catch(() => setBrandNames([]));
  }, []);

  const openBrandsMenu = useCallback(() => {
    setBrandsMenuOpen(true);
    ensureBrandNames();
  }, [ensureBrandNames]);

  // Closes the moment the pointer is off it. The word and the panel are one
  // hover area — the panel is a child of the item — so this fires on leaving
  // BOTH, not on crossing from the word down into the list.
  const closeBrandsMenu = useCallback(() => setBrandsMenuOpen(false), []);
  // onBlur bubbles (React maps it to focusout), so this fires for the links
  // inside too - only close once focus has left the item entirely.
  const handleBrandsMenuBlur = useCallback(
    (e) => {
      if (!e.currentTarget.contains(e.relatedTarget)) closeBrandsMenu();
    },
    [closeBrandsMenu]
  );
  const [drawerQuery, setDrawerQuery] = useState("");
  const [drawerSearchLoading, setDrawerSearchLoading] = useState(false);

  // Sync drawerQuery from URL when on search page
  useEffect(() => {
    if (location.pathname === "/featured/search") {
      const params = new URLSearchParams(location.search);
      const q = params.get("q") || "";
      if (q && !drawerSearchLoading) setDrawerQuery(q);
    } else if (!drawerSearchLoading) {
      setDrawerQuery("");
    }
  }, [location.pathname, location.search]); // eslint-disable-line react-hooks/exhaustive-deps
  const drawerFillRef = useRef(null);
  const [drawerExpanded, setDrawerExpanded] = useState(false);
  const [drawerFading, setDrawerFading] = useState(false);
  const [drawerToLanding, setDrawerToLanding] = useState(false);
  const [headerIdle, setHeaderIdle] = useState(false);
  const idleTimerRef = useRef(null);
  const pendingSearchRef = useRef(null);
  const keepDrawerOpenRef = useRef(false);
  const drawerSearchRef = useRef(null);

  // Fade out hamburger after 5s of inactivity. Desktop only: on touch there
  // is no pointer to wake it with, and the catalog title's arrows (which
  // body.page-idle fades) are the one cue that the title swipes.
  useEffect(() => {
    if (isTouchDevice()) return undefined;
    const resetIdle = () => {
      setHeaderIdle(false);
      document.body.classList.remove("page-idle");
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        setHeaderIdle(true);
        document.body.classList.add("page-idle");
      }, 5000);
    };
    resetIdle();
    window.addEventListener("mousemove", resetIdle);
    window.addEventListener("touchstart", resetIdle);
    window.addEventListener("scroll", resetIdle);
    window.addEventListener("keydown", resetIdle);
    return () => {
      clearTimeout(idleTimerRef.current);
      document.body.classList.remove("page-idle");
      window.removeEventListener("mousemove", resetIdle);
      window.removeEventListener("touchstart", resetIdle);
      window.removeEventListener("scroll", resetIdle);
      window.removeEventListener("keydown", resetIdle);
    };
  }, []);

  // Publish the live site-header height as a CSS variable so elements anchored
  // to the header's bottom edge (e.g. the audio visualizer strip) sit exactly
  // on the header line instead of a guessed offset below it.
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const setVar = () => {
      document.documentElement.style.setProperty(
        "--site-header-h",
        `${Math.round(el.offsetHeight)}px`
      );
    };
    setVar();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(setVar) : null;
    // The border box: the box whose height (offsetHeight) is published.
    ro?.observe(el, { box: "border-box" });
    window.addEventListener("resize", setVar);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", setVar);
    };
  }, []);

  const updateHighlightFromElement = useCallback((element) => {
    if (!element || !headerRef.current) {
      return;
    }
    const id = element.getAttribute("data-header-interaction") || null;
    const headerRect = headerRef.current.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    setSwoop({
      center: elementRect.left - headerRect.left + elementRect.width / 2,
      width: elementRect.width,
      height: elementRect.height,
      id,
      visible: true,
    });
  }, []);

  const resetHighlight = useCallback(() => {
    const headerNode = headerRef.current;
    if (!headerNode) {
      setSwoop((prev) => (prev.visible ? { center: 0, width: 0, height: 0, id: null, visible: false } : prev));
      return;
    }
    if (savedOpen) {
      const savedTrigger = headerNode.querySelector('[data-header-interaction="saved"]');
      if (savedTrigger) {
        updateHighlightFromElement(savedTrigger);
        return;
      }
    }
    const active = headerNode.querySelector("[data-header-interaction].is-active");
    if (active) {
      updateHighlightFromElement(active);
      return;
    }
    setSwoop({ center: 0, width: 0, height: 0, id: null, visible: false });
  }, [savedOpen, updateHighlightFromElement]);

  const handlePointerMove = useCallback(
    (event) => {
      if (!(event.target instanceof Element)) {
        return;
      }
      const candidate = event.target.closest("[data-header-interaction]");
      if (!candidate || !headerRef.current?.contains(candidate)) {
        return;
      }
      const candidateId = candidate.getAttribute("data-header-interaction") || null;
      if (swoop.visible && candidateId === swoop.id) {
        return;
      }
      updateHighlightFromElement(candidate);
    },
    [swoop.id, swoop.visible, updateHighlightFromElement],
  );

  const handlePointerLeave = useCallback(() => {
    resetHighlight();
  }, [resetHighlight]);

  const handleFocusCapture = useCallback(
    (event) => {
      if (!(event.target instanceof Element)) {
        return;
      }
      const candidate = event.target.closest("[data-header-interaction]");
      if (candidate && headerRef.current?.contains(candidate)) {
        updateHighlightFromElement(candidate);
      }
    },
    [updateHighlightFromElement],
  );

  const handleBlurCapture = useCallback(
    (event) => {
      const related = event.relatedTarget;
      if (related instanceof Element && headerRef.current?.contains(related)) {
        const candidate = related.closest("[data-header-interaction]");
        if (candidate) {
          updateHighlightFromElement(candidate);
          return;
        }
      }
      resetHighlight();
    },
    [resetHighlight, updateHighlightFromElement],
  );

  useEffect(() => {
    resetHighlight();
    // Ensure header is visible and scroll state is reset on route change
    setIsHeaderVisible(true);
    lastScrollY.current = 0;
  }, [location.pathname, user?.role, resetHighlight]);

  useEffect(() => {
    resetHighlight();
  }, [savedOpen, resetHighlight]);

  const isFeaturedPath = location.pathname.startsWith("/featured");

  useEffect(() => {
    const handleResize = () => {
      if (!swoop.visible || !swoop.id) {
        return;
      }
      const headerNode = headerRef.current;
      if (!headerNode) {
        return;
      }
      const current = headerNode.querySelector(`[data-header-interaction="${swoop.id}"]`);
      if (current) {
        updateHighlightFromElement(current);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [swoop.id, swoop.visible, updateHighlightFromElement]);

  useEffect(() => {
    // Hide on the way down, show on the way up. A few pixels of travel are
    // needed before it flips, so a finger resting on the glass (or a
    // trackpad's jitter) does not flicker the bar; and the rubber-band
    // bounce past the bottom of the page on iOS, which reads as an upward
    // scroll, is ignored.
    const FLIP_PX = 6;
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY <= 10) {
        setIsHeaderVisible(true);
        lastScrollY.current = currentScrollY;
        return;
      }

      const maxScrollY = document.documentElement.scrollHeight - window.innerHeight;
      if (currentScrollY >= maxScrollY - 2) {
        lastScrollY.current = Math.min(currentScrollY, maxScrollY);
        return;
      }

      const delta = currentScrollY - lastScrollY.current;
      if (Math.abs(delta) < FLIP_PX) return;
      setIsHeaderVisible(delta < 0);
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const swoopStyle = useMemo(() => {
    if (!swoop.visible) {
      return {
        "--header-swoop-opacity": 0,
      };
    }
    const paddedWidth = Math.max(Math.round(swoop.width + 28), Math.round(swoop.width * 1.25), 72);
    const paddedHeight = Math.max(Math.round(swoop.height + 16), 54);
    return {
      "--header-swoop-opacity": 1,
      "--header-swoop-left": `${swoop.center}px`,
      "--header-swoop-width": `${paddedWidth}px`,
      "--header-swoop-height": `${paddedHeight}px`,
    };
  }, [swoop]);

  const handleButtonPointerEnter = useCallback(
    (event) => {
      const button = event.currentTarget;
      if (button && button.hasAttribute("data-header-interaction")) {
        updateHighlightFromElement(button);
      }
    },
    [updateHighlightFromElement],
  );

  const handleButtonPointerLeave = useCallback(
    (event) => {
      const related = event.relatedTarget;
      if (related instanceof Element) {
        const nextCandidate = related.closest("[data-header-interaction]");
        if (nextCandidate && headerRef.current?.contains(nextCandidate)) {
          updateHighlightFromElement(nextCandidate);
          return;
        }
      }
      resetHighlight();
    },
    [resetHighlight, updateHighlightFromElement],
  );


  // Close menu on route change
  useEffect(() => {
    if (pendingSearchRef.current) {
      // Search navigation — fade out after title renders
      pendingSearchRef.current = null;
      setDrawerFading(true);
      const timer = setTimeout(() => {
        setMenuOpen(false);
        setDrawerFading(false);
        setDrawerExpanded(false);
      }, 500);
      return () => clearTimeout(timer);
    }
    if (keepDrawerOpenRef.current) {
      keepDrawerOpenRef.current = false;
      return;
    }
    setMenuOpen(false);
    setDrawerSearchOpen(false);
    setDrawerExpanded(false);
  }, [location.pathname, location.search]);

  // Lock page scroll while the drawer is open (utils/scrollLock: html and
  // body, which is what iOS Safari honours; reference counted, so the search
  // modal opening over the drawer cannot release it early).
  useEffect(() => {
    if (!menuOpen) return undefined;
    lockScroll();
    return () => unlockScroll();
  }, [menuOpen]);

  // Sidebar BRANDS sub-link: close the drawer, then tell the brand page WHICH
  // brand to land on through brandNavSignal. If the carousel is already
  // mounted (we are on /brand) it moves at once; otherwise the request is
  // parked and consumed when it mounts. navigate() only when not already
  // there, so picking brands from the sidebar does not pile up history.
  const handleMenuNavigateBrand = useCallback((name) => {
    setMenuOpen(false);
    setCatalogOpen(false);
    setBrandsSubOpen(false);
    setDrawerExpanded(false);
    requestBrandNav(name);
    if (location.pathname !== "/showcase") navigate("/showcase");
  }, [navigate, location.pathname]);

  // Whatever closes the drawer (toggle, scrim, route change) also folds the
  // BRANDS list, so it never reopens already expanded.
  useEffect(() => {
    if (!menuOpen) setBrandsSubOpen(false);
  }, [menuOpen]);

  // state: optional route state for the destination.
  const handleMenuNavigate = useCallback((path, state) => {
    setMenuOpen(false);
    setCatalogOpen(false);
    setDrawerExpanded(false);
    // A catalog page on a category (TOPS) or with sizes picked takes its own
    // way out first: the title fades back to FEATURED, then it navigates
    // (FeaturedPage, "catalog-leave"). It cancels the event when it does.
    if (path.startsWith("/featured")) {
      const ask = new CustomEvent("catalog-leave", { detail: { path, state }, cancelable: true });
      if (!window.dispatchEvent(ask)) return;
    }
    navigate(path, state ? { state } : undefined);
  }, [navigate]);

  // HOME from the bar or the mobile dropdown: an ordinary route change, the
  // same 150ms page cross-fade as catalog -> brand. It used to run the
  // fade-to-black overlay (startNavFade in HeaderContext); the landing page
  // no longer gets a special entrance from anywhere.
  const goToLanding = useCallback(() => {
    setMenuOpen(false);
    setDrawerExpanded(false);
    setDrawerToLanding(false);
    // Already home: HOME and the mark answer with a return to the top, as
    // CATALOG does on the catalog, rather than a navigation to where we are.
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    navigate("/");
  }, [navigate, location.pathname]);

  // Brands MODE (the fixed, clear bar) follows the page on screen, not the
  // live route: see utils/displayedRoute. Keyed on the live route it went
  // fixed while the old page was still fading out, and that page jumped up
  // under it. The current-page marks keep reading the live route, so they
  // still answer the click at once.
  const displayedPath = useSyncExternalStore(subscribeDisplayedPath, getDisplayedPath, getDisplayedPath);
  const brandsModeShown = displayedPath.startsWith("/showcase");
  const isHomeRoute = location.pathname === "/";
  // On a phone the bar never leaves the screen: not on a scroll down, not
  // under the catalog's stuck title, not on the showcase, whatever a page
  // asks for. Desktop keeps its hide-on-scroll-down and the tuck under the
  // catalog's stuck title bar.
  const barCollapsed = collapsed && !isMobile;
  const barScrolledAway = !isHeaderVisible && !isMobile;
  // The fixed hamburger goes when the bar tucks under the stuck title
  // (desktop only, per the above), but never while it is the X that closes
  // the open drawer.
  const toggleHidden = !menuOpen && barCollapsed;

  // The phone search icon is also the search's close control while it is up.
  const searchShown = useSyncExternalStore(subscribeSearchModalShown, getSearchModalShown, getSearchModalShown);
  // While the showcase list is up (SearchModal's brands mode), SHOWCASE is
  // what the screen shows, so the lit word and its underline move to it from
  // the page's own section, and move back as the list closes. Only the mark
  // moves: aria-current and a section's press-to-scroll-up stay with the
  // route, since the page underneath has not changed.
  const searchMode = useSyncExternalStore(subscribeSearchModalShown, getSearchModalMode, getSearchModalMode);
  const showcaseListUp = searchShown && searchMode === "brands";
  const handlePhoneSearch = useCallback(() => {
    // Up, the icon is the X, and it closes the overlay whichever mode it is
    // in, the showcase list opened from the drawer (openShowcaseList) as
    // well: a toggle would switch that list over to the search instead.
    if (searchShown) {
      closeSearchModal();
      return;
    }
    // Opening from a tap: bring the keyboard up now, inside the tap, so it
    // is already there when the search field takes focus (utils/softKeyboard).
    if (isTouchDevice()) primeSoftKeyboard();
    toggleSearchModal();
  }, [searchShown]);

  // The drawer's SHOWCASE on a phone: the showcase list over the page, the
  // brand tiles the desktop header's SHOWCASE opens (SearchModal's brands
  // mode), in place of the drawer's own list of names. The drawer closes
  // under it; it was open, so the overlay was not, and the toggle opens it.
  const openShowcaseList = useCallback(() => {
    setMenuOpen(false);
    setCatalogOpen(false);
    setDrawerExpanded(false);
    toggleShowcaseModal();
  }, []);

  // A phone's drawer, once open, fetches the showcase list ahead of its
  // SHOWCASE: the brands and where each backdrop lives, about 11 KB, so a
  // first SHOWCASE from the drawer opens on the names at once, as a reopen
  // does, where a cold one showed plain tiles for 0.7-1.2s. Only the list:
  // the backdrops themselves (seven full-size PNGs, about 7 MB) still load
  // when the list opens, not on every look at the drawer.
  useEffect(() => {
    if (menuOpen && isMobile) fetchBrandList();
  }, [menuOpen, isMobile]);

  return (
    <>
    <header
      ref={headerRef}
      className={`app-header ${!user ? "app-header--guest" : ""} ${barScrolledAway ? "app-header--hidden" : ""} ${barCollapsed ? "app-header--collapsed" : ""}${loadingPhase === "loading" ? " app-header--loading" : ""}${loadingPhase === "finishing" ? " app-header--loading-finish" : ""}${loadingPhase === "sweep" ? " app-header--loading-sweep" : ""}${headerIdle ? " app-header--idle" : ""}${brandsModeShown ? " app-header--brands" : ""}${landingIntro ? " app-header--landing-intro" : ""}${isHomeRoute ? " app-header--home" : ""}`}
      data-swoop-visible={swoop.visible ? "true" : "false"}
      data-swoop-target={swoop.id || "none"}
      data-active-section={HEADER_PAGES[activePageIndex]?.key || ""}
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
      onFocusCapture={handleFocusCapture}
      onBlurCapture={handleBlurCapture}
    >
      <span className="app-header__swoop" aria-hidden="true" style={swoopStyle} />
      <div className="app-header__section app-header__section--left">
        {/* The hamburger is the fixed .sidebar-toggle below, at every width.
            The phone used to keep an in-bar chevron here that dropped a
            three-item strip (HOME / SHOWCASE / CATALOG); the strip only
            duplicated the wheel, while the drawer — search, sections, the
            brand list, info, contact, currency — could not be opened on a
            phone at all. Now one control opens one menu everywhere. */}
        {/* Desktop: the section links live in the left cell, immediately right
            of the hamburger, reading HOME · CATALOG · BRANDS. They used to be
            absolutely centred in the bar via .app-header__brand-center, which
            is now the mobile slot wheel's alone. */}
        {!isMobile && (
          <nav className="app-header__nav" aria-label="Sections">
            {HEADER_NAV.map(({ page, index }, pos) => {
              // activePageIndex falls back to the last-known section when the
              // route matches none (the mobile slot wheel needs a valid
              // index). The landing page is a page of its own, so on "/" no
              // section is current: HOME below takes the mark instead.
              const active = !isHomeRoute && onSectionPage && index === activePageIndex;
              const lit = showcaseListUp ? page.key === "brand" : active;
              const pending = pendingNav === page.key;
              const sectionLink = (
                <button
                  key={page.key}
                  type="button"
                  className={`app-header__nav-link${lit && !pending ? " app-header__nav-link--active" : ""}${pending ? " app-header__nav-link--pending" : ""}`}
                  aria-current={active ? "page" : undefined}
                  data-header-interaction={`nav-${page.key}`}
                  onClick={() => {
                    // SHOWCASE opens the showcase list: the search's own glass
                    // with the brands in place of the bar (SearchModal's
                    // brands mode), over /showcase itself, which the page
                    // behind goes to on a random brand. Its names show their
                    // brand there; ALL BRANDS just closes it. Pressed again,
                    // it closes.
                    if (page.key === "brand") {
                      // Only opening the list waits for the bar; closing it
                      // hands the mark back to the page's own section.
                      if (!showcaseListUp) startNavPending(page.key);
                      toggleShowcaseModal();
                      return;
                    }
                    // Pressed on its own page, a section scrolls back up. CATALOG
                    // counts as on its page only on the catalog views themselves
                    // (FEATURED, NEW, SOLD): from a piece, the cart, checkout or
                    // anything else under /featured it goes back to the catalog
                    // (the owner, 2026-10-02).
                    const onSectionPage = page.key !== "featured"
                      || CATALOG_VIEWS.has(location.pathname.replace(/\/+$/, ""));
                    if (active && onSectionPage) {
                      window.scrollTo({ top: 0, behavior: "smooth" });
                      return;
                    }
                    startNavPending(page.key);
                    handlePageSlotChange(index);
                  }}
                  onPointerEnter={(e) => {
                    // Pointing at SHOWCASE starts loading its list and the
                    // tiles' backdrops (utils/brandList warmShowcase), so a
                    // first press after a reload finds them ready.
                    if (page.key === "brand") warmShowcase();
                    handleButtonPointerEnter(e);
                  }}
                  onFocus={page.key === "brand" ? () => { warmShowcase(); } : undefined}
                  onPointerLeave={handleButtonPointerLeave}
                >
                  {page.label}
                </button>
              );
              // BRANDS carries the hover menu; every other link is bare.
              // With the flag off it is bare too, so the bar is exactly the
              // three plain links it was before the menu existed.
              const navItem = !BRANDS_MENU_ENABLED || page.key !== "brand" ? sectionLink : (
                <div
                  key={page.key}
                  className="app-header__nav-item"
                  onPointerEnter={openBrandsMenu}
                  onPointerLeave={closeBrandsMenu}
                  onFocus={openBrandsMenu}
                  onBlur={handleBrandsMenuBlur}
                >
                  {sectionLink}
                  <div
                    className={`app-header__brands-menu${brandsMenuOpen && brandNames.length ? " app-header__brands-menu--open" : ""}`}
                  >
                    <div className="app-header__brands-menu-inner">
                      {brandNames.map((name) => (
                        <button
                          key={name}
                          type="button"
                          className="app-header__brands-menu-item"
                          tabIndex={brandsMenuOpen ? 0 : -1}
                          onClick={() => {
                            closeBrandsMenu();
                            // BrandCarousel consumes state.targetBrand and
                            // scrolls that brand's row into view.
                            requestBrandNav(name);
                            if (location.pathname !== "/showcase") navigate("/showcase");
                          }}
                        >
                          {name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              );
              if (pos !== 0) return navItem;
              // HOME is rendered INSIDE the map, straight BEFORE the first
              // section link, so the bar reads HOME · CATALOG · BRANDS.
              return (
                <React.Fragment key={page.key}>
                  <button
                    type="button"
                    className={`app-header__nav-link${isHomeRoute && !showcaseListUp && pendingNav !== "home" ? " app-header__nav-link--active" : ""}${pendingNav === "home" ? " app-header__nav-link--pending" : ""}`}
                    aria-current={isHomeRoute ? "page" : undefined}
                    data-header-interaction="nav-home"
                    onClick={() => {
                      if (!isHomeRoute) startNavPending("home");
                      goToLanding();
                    }}
                    onPointerEnter={handleButtonPointerEnter}
                    onPointerLeave={handleButtonPointerLeave}
                  >
                    HOME
                  </button>
                  {navItem}
                </React.Fragment>
              );
            })}
          </nav>
        )}
        {user && <SavedWidget />}
        {(user?.role === "admin" || user?.role === "owner") && (
          <button
            type="button"
            className={location.pathname === "/admin" ? "app-header__link is-active" : "app-header__link"}
            data-header-interaction="admin"
            onClick={() => navigate("/admin")}
            onPointerEnter={handleButtonPointerEnter}
            onPointerLeave={handleButtonPointerLeave}
          >
            Admin
          </button>
        )}
      </div>
      {/* The centred mark, at every width. .app-header__brand-center is
          absolute across the header's whole content box, so the mark lands on
          the BAR's midpoint rather than between the left and right groups.
          Phones used to carry a swipeable HOME / CATALOG / SHOWCASE wheel here;
          the sidebar reaches every section (and the brands, the catalog views,
          search, info, contact and currency), so the bar keeps just the mark,
          as desktop does. */}
      <div className="app-header__brand-center">
        <div className="app-header__slot-nav">
          {/* The mark is the way home: the same plain route change HOME
              makes (goToLanding). The image itself stays decorative; the
              button carries the name. */}
          <button
            type="button"
            className="app-header__logo-btn"
            aria-label="itrapswag home"
            aria-current={isHomeRoute ? "page" : undefined}
            data-header-interaction="logo-home"
            onClick={goToLanding}
            onPointerEnter={handleButtonPointerEnter}
            onPointerLeave={handleButtonPointerLeave}
          >
            <img
              src={faviconUrl}
              className="app-header__logo"
              alt=""
              aria-hidden="true"
              draggable={false}
            />
          </button>
        </div>
      </div>
      <div className="app-header__section app-header__section--right">
        {!isMobile && (
          <>
            {/* Currency sits immediately left of the search icon, at the head
                of the right-hand control group. */}
            <CurrencySelector
              onPointerEnter={handleButtonPointerEnter}
              onPointerLeave={handleButtonPointerLeave}
            />
            <button
              type="button"
              className="app-header__icon-btn app-header__icon-btn--search"
              data-header-interaction="search"
              aria-label="Search the catalog"
              onClick={toggleSearchModal}
              onPointerEnter={handleButtonPointerEnter}
              onPointerLeave={handleButtonPointerLeave}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                <circle cx="11" cy="11" r="7" />
                <line x1="16.5" y1="16.5" x2="21" y2="21" />
              </svg>
            </button>
          </>
        )}
        {isMobile && (
          /* Phone: search in the bar, beside the cart. Opens the site search
             modal (utils/searchModalSignal), the same surface the desktop
             corner icon opens; the modal's own phone branch navigates on
             Enter without the fill animation. While the search is up this
             same button is its close control, drawn as an X. */
          <button
            type="button"
            className="app-header__icon-btn app-header__icon-btn--search"
            data-header-interaction="search"
            aria-label={searchShown ? "Close search" : "Search the catalog"}
            aria-expanded={searchShown}
            onClick={handlePhoneSearch}
          >
            {searchShown ? (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" focusable="false">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            ) : (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                <circle cx="11" cy="11" r="7" />
                <line x1="16.5" y1="16.5" x2="21" y2="21" />
              </svg>
            )}
          </button>
        )}
        <CartWidget />
      </div>
    </header>
    {/* The frost behind the bar: a sibling rather than a child, so it can
        fade out toward the bar's bottom edge AND still blur while the bar
        itself fades in (.app-header-frost in app.css). Must stay the bar's
        next sibling: its hide states follow the bar's classes through ~. */}
    <div className="app-header-frost" aria-hidden="true" />
    {/* The hamburger, at every width. Outside <header> for the same reason
        the drawer and its backdrop are: the header is a sticky stacking
        context, so a button inside it can never paint above the open sidebar
        no matter what z-index it carries. It is fixed at the header's own
        inset, so at rest it reads as part of the bar. On phones it leaves
        with the bar (scrolling down hides the bar; the catalog tucks it away
        once its title is stuck) rather than floating on over the page. */}
    <button
      type="button"
      className={`sidebar-toggle${menuOpen ? " sidebar-toggle--open" : ""}${toggleHidden ? " sidebar-toggle--hidden" : ""}`}
      aria-label={menuOpen ? "Close menu" : "Open menu"}
      aria-expanded={menuOpen}
      onClick={() => setMenuOpen((prev) => !prev)}
    >
      <span className="sidebar-toggle__bar" />
      <span className="sidebar-toggle__bar" />
      <span className="sidebar-toggle__bar" />
    </button>
    {/* Currency, search and cart on every page, rendered OUT HERE beside
        .sidebar-toggle rather than in the bar — the same reason the toggle
        lives here. The bar is a stacking context, and hiding it takes its
        position: fixed children with it, so styling the header's own right
        section could never keep these on screen once it scrolled or tucked
        away. Out here they behave exactly as the hamburger does.
        It carries --right as well so the spacing between the three comes from
        the bar's own rule rather than a copy of it; the bar's copy of that
        section is hidden (see app.css) so there is only ever one of each.
        Leaves with the hamburger on the same condition, so the corner goes
        and returns as one set rather than the icons outliving the menu. */}
    {!isMobile && (
      <div className={`app-header__section app-header__section--right corner-actions${collapsed && !menuOpen ? " corner-actions--hidden" : ""}`}>
        {/* Music, at the head of the right-hand group, left of the
            currency (the owner, 2026-10-17). The same note as before at
            rest; under the pointer (or keyboard focus) it fades to what
            a press will do: play, or pause while a track is on. */}
        {(MUSIC_ENABLED || MUSIC_HEADER_ENABLED) && (
          <button
            type="button"
            className={`app-header__icon-btn app-header__icon-btn--audio${audioPlaying ? " app-header__icon-btn--on" : ""}`}
            data-header-interaction="audio"
            aria-label={audioPlaying ? "Pause music" : "Play music"}
            aria-pressed={audioPlaying}
            onClick={() => toggleAudio && toggleAudio()}
          >
            {/* The site's first music icons (the floating toggle, April
                2026): the filled single note and the filled play triangle,
                with a filled pause drawn to match (that toggle showed
                visualizer bars while playing, not a pause). */}
            <span className="app-header__music-icons" aria-hidden="true">
              <svg className="app-header__music-icon app-header__music-icon--note" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" focusable="false">
                <path d="M12 3v10.55A4 4 0 1 0 14 17V7h4V3z" />
              </svg>
              <svg className="app-header__music-icon app-header__music-icon--play" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" focusable="false">
                <path d="M7 5 L19 12 L7 19 Z" />
              </svg>
              <svg className="app-header__music-icon app-header__music-icon--pause" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" focusable="false">
                <path d="M7 5h3.5v14H7zM13.5 5H17v14h-3.5z" />
              </svg>
            </span>
          </button>
        )}
        <CurrencySelector />
        <button
          type="button"
          className="app-header__icon-btn"
          data-header-interaction="search"
          aria-label="Search the catalog"
          onClick={toggleSearchModal}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
            <circle cx="11" cy="11" r="7" />
            <line x1="16.5" y1="16.5" x2="21" y2="21" />
          </svg>
        </button>
        <CartWidget />
      </div>
    )}
    <div
      className={`nav-drawer-backdrop${menuOpen ? " nav-drawer-backdrop--open" : ""}`}
      onClick={() => { setMenuOpen(false); setDrawerExpanded(false); }}
      aria-hidden="true"
    />
    <nav
      className={`nav-drawer${menuOpen ? " nav-drawer--open" : ""}${drawerExpanded ? " nav-drawer--expanded" : ""}${drawerFading ? " nav-drawer--fading" : ""}${drawerToLanding ? " nav-drawer--to-landing" : ""}`}
        aria-label="Main navigation"
        aria-hidden={!menuOpen}
      >
        <div className="nav-drawer__top">
          <span className="nav-drawer__label">MENU</span>
          {/* No close button in here: the fixed .sidebar-toggle rides out to the
              sidebar edge and folds into a cross, and that IS the close control.
              A second X inside the panel only duplicated it. */}
        </div>
        <div className="nav-drawer__links">
          {/* HOME first: on a phone the drawer is the only menu, and the bar's
              HOME link is desktop-only. goToLanding closes the drawer itself. */}
          <button
            type="button"
            className="nav-drawer__link nav-drawer__link--home"
            aria-current={isHomeRoute ? "page" : undefined}
            onClick={goToLanding}
          >
            HOME
          </button>
          {/* CATALOG first, then SHOWCASE. */}
          <button type="button" className="nav-drawer__link nav-drawer__link--catalog" onClick={() => setCatalogOpen((prev) => !prev)}>
            CATALOG
            <svg className={`nav-drawer__catalog-chevron${catalogOpen ? " nav-drawer__catalog-chevron--open" : ""}`} xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.43" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          <div className={`nav-drawer__catalog-sub${catalogOpen ? " nav-drawer__catalog-sub--open" : ""}`}>
            <button type="button" className="nav-drawer__link nav-drawer__link--sub" onClick={() => handleMenuNavigate("/featured")}>FEATURED</button>
            <button type="button" className="nav-drawer__link nav-drawer__link--sub" onClick={() => (location.pathname === "/featured/new"
              ? handleMenuNavigate("/featured/new")
              // Lands on FEATURED, then the catalog turns its own title wheel
              // on to NEW ARRIVALS (FeaturedPage, slideTo), so the move between
              // the two is seen (the owner, 2026-10-02).
              : handleMenuNavigate("/featured", { slideTo: "new" }))}>NEW ARRIVALS</button>
            {/* The catalog by category, as the home page's SHOP BY tiles link it. */}
            <button type="button" className="nav-drawer__link nav-drawer__link--sub" onClick={() => handleMenuNavigate("/featured?cat=tops")}>TOPS</button>
            <button type="button" className="nav-drawer__link nav-drawer__link--sub" onClick={() => handleMenuNavigate("/featured?cat=bottoms")}>BOTTOMS</button>
            <button type="button" className="nav-drawer__link nav-drawer__link--sub" onClick={() => handleMenuNavigate("/featured?cat=shoes")}>SHOES</button>
            <button type="button" className="nav-drawer__link nav-drawer__link--sub" onClick={() => handleMenuNavigate("/featured?cat=accessories")}>ACCESSORIES</button>
            <button type="button" className="nav-drawer__link nav-drawer__link--sub" onClick={() => handleMenuNavigate("/featured/sold")}>SOLD</button>
          </div>
          {/* SHOWCASE dropdown: the CATALOG dropdown above, class for class, so
              the two behave and animate identically. First entry keeps the
              showcase overview reachable; the rest are the live brand list.
              On a phone it opens the showcase list over the page instead
              (openShowcaseList), the tiles the desktop header's SHOWCASE
              opens, so there it has no dropdown and no chevron. */}
          <button type="button" className="nav-drawer__link nav-drawer__link--catalog" onClick={isMobile ? openShowcaseList : () => { ensureBrandNames(); setBrandsSubOpen((prev) => !prev); }}>
            SHOWCASE
            {!isMobile && (
              <svg className={`nav-drawer__catalog-chevron${brandsSubOpen ? " nav-drawer__catalog-chevron--open" : ""}`} xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.43" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 9l6 6 6-6" />
              </svg>
            )}
          </button>
          {!isMobile && (
            <div className={`nav-drawer__catalog-sub nav-drawer__catalog-sub--brands${brandsSubOpen ? " nav-drawer__catalog-sub--open" : ""}`}>
              <button type="button" className="nav-drawer__link nav-drawer__link--sub" onClick={() => handleMenuNavigate("/showcase")}>VIEW ALL</button>
              {brandNames.map((name) => (
                <button key={name} type="button" className="nav-drawer__link nav-drawer__link--sub" onClick={() => handleMenuNavigateBrand(name)}>{name}</button>
              ))}
            </div>
          )}
          <div className={`nav-drawer__link nav-drawer__link--search${drawerSearchLoading ? " nav-drawer__link--search-loading" : ""}`} onClick={() => {
            if (!drawerSearchOpen && !drawerSearchLoading) {
              // Clear old query and reset page to featured
              if (drawerQuery) {
                setDrawerQuery("");
                keepDrawerOpenRef.current = true;
                navigate("/featured");
              }
              setDrawerSearchOpen(true);
              setTimeout(() => drawerSearchRef.current?.focus(), 50);
            }
          }}>
            {drawerSearchLoading && (
              <span className="nav-drawer__search-loading">{drawerQuery}</span>
            )}
            {!drawerSearchLoading && !drawerSearchOpen && drawerQuery && (
              <>
                <span className="nav-drawer__search-query">{drawerQuery}</span>
                <button
                  type="button"
                  className="nav-drawer__search-cancel"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDrawerQuery("");
                    keepDrawerOpenRef.current = true;
                    navigate("/featured");
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </>
            )}
            <input
              ref={drawerSearchRef}
              type="search"
              enterKeyHint="search"
              className="nav-drawer__search-inline"
              placeholder="SEARCH"
              value={drawerQuery}
              onChange={(e) => setDrawerQuery(e.target.value)}
              onBlur={() => {}}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setDrawerSearchOpen(false);
                  setDrawerQuery("");
                } else if (e.key === "Enter" && drawerQuery.trim() && !drawerSearchLoading) {
                  e.preventDefault();
                  const term = drawerQuery.trim();
                  if (isMobile) {
                    // Mobile: skip the white fill animation and the 1.5s
                    // hold — close the drawer and navigate immediately.
                    setDrawerSearchOpen(false);
                    setMenuOpen(false);
                    setDrawerExpanded(false);
                    navigate(`/featured/search?q=${encodeURIComponent(term)}`);
                    return;
                  }
                  setDrawerSearchLoading(true);
                  setDrawerSearchOpen(false);
                  // Navigate in background
                  keepDrawerOpenRef.current = true;
                  navigate(`/featured/search?q=${encodeURIComponent(term)}`);
                  // White loading animation
                  setTimeout(() => {
                    // Set on the ROW, not the text span: custom properties
                    // inherit downward, so the span still picks it up while
                    // the row's ::after rule can read it too and fill in step
                    // with the letters. Reading it off the span left the line
                    // with no access to it at all.
                    const el = document.querySelector(".nav-drawer__link--search-loading");
                    if (!el) {
                      setTimeout(() => { setDrawerSearchLoading(false); setMenuOpen(false); }, 1500);
                      return;
                    }
                    let fill = 0;
                    // Paint the empty state before the first jump lands.
                    // tick() used to run inline, and its opening jump is up
                    // to ~15%, so the fill visibly began part-way along and
                    // the word never showed unfilled.
                    el.style.setProperty("--search-fill", "0%");
                    const minDuration = 1500;
                    const startTime = Date.now();
                    const tick = () => {
                      const elapsed = Date.now() - startTime;
                      const remaining = 100 - fill;
                      const jump = Math.random() * Math.max(1, remaining * 0.15) + 0.5;
                      fill = Math.min(fill + jump, 100);
                      el.style.setProperty("--search-fill", fill + "%");
                      if (fill < 100) {
                        drawerFillRef.current = setTimeout(tick, Math.random() * 80 + 20);
                      } else {
                        const wait = Math.max(400, minDuration - elapsed + 400);
                        drawerFillRef.current = setTimeout(() => {
                          setDrawerSearchLoading(false);
                          setMenuOpen(false);
                          setDrawerExpanded(false);
                        }, wait);
                      }
                    };
                    // One frame at 0% before it moves.
                    drawerFillRef.current = setTimeout(tick, 60);
                  }, 50);
                }
              }}
              tabIndex={drawerSearchOpen ? 0 : -1}
              readOnly={!drawerSearchOpen}
            />
          </div>
        </div>
        {/* Footer: wordmark + Instagram, a rule, two chevron rows, then the
            currency pill and the copyright side by side. The drawer is a flex
            column and .nav-drawer__links is centred by auto margins, so this
            block stacks against the bottom edge on its own, and the drawer
            scrolls when the rows outgrow the screen. */}
        <div className="nav-drawer__foot">
          <div className="nav-drawer__foot-brand">
            {/* The wordmark IS the contact link: it mails contact@itrapswag.com. */}
            <a className="nav-drawer__foot-wordmark" href="mailto:contact@itrapswag.com">itrapswag</a>
            <a
              href="https://www.instagram.com/itrapswag/"
              target="_blank"
              rel="noopener noreferrer"
              className="nav-drawer__foot-social"
              aria-label="Instagram"
            >
              {/* Centred in the link's 34px box rather than filling it, so the
                  row's alignment and the tap target are unchanged. strokeWidth
                  is in viewBox units: 1.2 at this size renders ~1.4px, near the
                  weight the glyph drew back when it sat inside a border. */}
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                <rect x="2" y="2" width="20" height="20" rx="5" />
                <circle cx="12" cy="12" r="4.2" />
                <circle cx="17.5" cy="6.5" r="1.1" fill="currentColor" stroke="none" />
              </svg>
            </a>
          </div>
          <div className="nav-drawer__foot-links">
            {DRAWER_FOOT_LINKS.map((l) => {
              const chevron = (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.43" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                  <path d="M9 6l6 6-6 6" />
                </svg>
              );
              // A mailto row is a real <a>, not a button with a JS redirect:
              // the browser then treats it as a link the reader can long-press
              // or copy, and a device with no mail client set simply does
              // nothing rather than navigating the app somewhere blank.
              // The drawer still closes, since the page itself will not change.
              if (l.mailto) {
                return (
                  <a
                    key={l.label}
                    className="nav-drawer__foot-link"
                    href={`mailto:${l.mailto}`}
                    onClick={() => { setMenuOpen(false); setCatalogOpen(false); setDrawerExpanded(false); }}
                  >
                    <span>{l.label}</span>
                    {chevron}
                  </a>
                );
              }
              return (
                <button key={l.label} type="button" className="nav-drawer__foot-link" onClick={() => handleMenuNavigate(l.path)}>
                  <span>{l.label}</span>
                  {chevron}
                </button>
              );
            })}
          </div>
          <div className="nav-drawer__foot-bottom">
            <div className="nav-drawer__foot-currency">
              <CurrencySelector />
            </div>
            <span className="nav-drawer__copyright">
              © {new Date().getFullYear()} itrapswag
            </span>
          </div>
        </div>
      </nav>
    </>
  );
}
